import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { BoutiqueProvider } from './context/BoutiqueContext';
import { ConfigProvider, useConfig } from './context/ConfigContext';

// Pages
import HomePage from './pages/HomePage';
import Login from './pages/Login';
import Dashboard from './pages/DashboardFeveo';
import DashboardEntreprise from './pages/DashboardEntreprise';
import DashboardCaissier from './pages/DashboardCaissier';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import SaaSManagement from './pages/SaaSManagement';
import PlansManagement from './pages/PlansManagement';
import Produits from './pages/ProduitsFeveo';
import Stock from './pages/Stock';
import Fournisseurs from './pages/Fournisseurs';
import CommandesFournisseurs from './pages/CommandesFournisseurs';
import DettesFournisseurs from './pages/DettesFournisseurs';
import Transferts from './pages/Transferts';
import Entrepots from './pages/Entrepots';
import Inventaire from './pages/Inventaire';
import Ventes from './pages/Ventes';
import NouvelleVente from './pages/NouvelleVente';
import RetoursVentes from './pages/RetoursVentes';
import Factures from './pages/Factures';
import CaisseJournaliere from './pages/CaisseJournaliere';
import Utilisateurs from './pages/Utilisateurs';
import Configuration from './pages/Configuration';
import Boutiques from './pages/Boutiques';
import Vitrine from './pages/Vitrine';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import Dettes from './pages/Dettes';
import Register from './pages/Register';
import Shop from './pages/Shop';
import MarketingCRM from './pages/MarketingCRM';
import CreativeStudio from './pages/CreativeStudio';
import PublishingQueue from './pages/PublishingQueue';
import AttributionDashboard from './pages/AttributionDashboard';
import EcommerceVitrines from './pages/ecommerce/EcommerceVitrines';
import EcommerceCommandes from './pages/ecommerce/EcommerceCommandes';
import EcommerceImportStock from './pages/ecommerce/EcommerceImportStock';
import LoyaltyCards from './pages/loyalty/LoyaltyCards';
import LoyaltyRewards from './pages/loyalty/LoyaltyRewards';
import LoyaltyParametres from './pages/loyalty/LoyaltyParametres';

// Composants
import Layout from './components/LayoutFeveo';
import PrivateRoute from './components/PrivateRoute';

// Garde d'accès pour le module Marketing : redirige si le module est désactivé
const MarketingRoute = ({ children }) => {
  const { marketingEnabled, loading } = useConfig();
  if (loading) return null;
  return marketingEnabled ? children : <Navigate to="/dashboard" replace />;
};

// Garde d'accès pour le module E-commerce : redirige si le module est désactivé
const EcommerceRoute = ({ children }) => {
  const { ecommerceEnabled, loading } = useConfig();
  if (loading) return null;
  return ecommerceEnabled ? children : <Navigate to="/dashboard" replace />;
};

// Garde d'accès pour le module Fidélité : redirige si le module est désactivé
const LoyaltyRoute = ({ children }) => {
  const { loyaltyEnabled, loading } = useConfig();
  if (loading) return null;
  return loyaltyEnabled ? children : <Navigate to="/dashboard" replace />;
};

// Composant pour router vers le bon dashboard
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === 'admin-entreprise' || user?.role === 'admin-pro') return <DashboardEntreprise />;
  if (user?.role === 'caissier') return <DashboardCaissier />;
  return <Dashboard />;
};

function App() {
  const { loading } = useAuth();

  if (loading) {  
  
  // Dans vos routes :
  
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Chargement...</div>
      </div>
    );
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ConfigProvider>
      <BoutiqueProvider>
        <Routes>
          {/* Page d'accueil publique */}
          <Route path="/home" element={<HomePage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Vitrine publique d'une boutique (commande en ligne, paiement à la livraison) */}
          <Route path="/shop/:id" element={<Shop />} />
          
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardRouter />} />
            <Route path="super-admin" element={<SuperAdminDashboard />} />
            <Route path="saas-management" element={<SaaSManagement />} />
            <Route path="plans-management" element={<PlansManagement />} />
            <Route path="boutiques" element={<Boutiques />} />
            <Route path="boutiques/:id/vitrine" element={<Vitrine />} />
            <Route path="produits" element={<Produits />} />
            <Route path="stock" element={<Stock />} />
            <Route path="fournisseurs" element={<Fournisseurs />} />
            <Route path="commandes-fournisseurs" element={<CommandesFournisseurs />} />
            <Route path="dettes-fournisseurs" element={<DettesFournisseurs />} />
            <Route path="transferts-stock" element={<Transferts />} />
            <Route path="entrepots" element={<Entrepots />} />
            <Route path="inventaire" element={<Inventaire />} />
            <Route path="clients" element={<Clients />} />
            <Route path="clients/:id" element={<ClientDetails />} />
            <Route path="dettes" element={<Dettes />} />
            <Route path="caisse" element={<CaisseJournaliere />} />
            <Route path="ventes" element={<Ventes />} />
            <Route path="ventes/nouvelle" element={<NouvelleVente />} />
            <Route path="retours" element={<RetoursVentes />} />
            <Route path="factures" element={<Factures />} />
            <Route path="marketing/crm" element={<MarketingRoute><MarketingCRM /></MarketingRoute>} />
            <Route path="marketing/creative" element={<MarketingRoute><CreativeStudio /></MarketingRoute>} />
            <Route path="marketing/publishing" element={<MarketingRoute><PublishingQueue /></MarketingRoute>} />
            <Route path="marketing/attribution" element={<MarketingRoute><AttributionDashboard /></MarketingRoute>} />
            <Route path="ecommerce/vitrines" element={<EcommerceRoute><EcommerceVitrines /></EcommerceRoute>} />
            <Route path="ecommerce/commandes" element={<EcommerceRoute><EcommerceCommandes /></EcommerceRoute>} />
            <Route path="ecommerce/import-stock" element={<EcommerceRoute><EcommerceImportStock /></EcommerceRoute>} />
            <Route path="loyalty/cartes" element={<LoyaltyRoute><LoyaltyCards /></LoyaltyRoute>} />
            <Route path="loyalty/recompenses" element={<LoyaltyRoute><LoyaltyRewards /></LoyaltyRoute>} />
            <Route path="loyalty/parametres" element={<LoyaltyRoute><LoyaltyParametres /></LoyaltyRoute>} />
            <Route path="utilisateurs" element={<Utilisateurs />} />
            <Route path="configuration" element={<Configuration />} />
          </Route>
        </Routes>
      </BoutiqueProvider>
      </ConfigProvider>
    </Router>
  );
}

export default App;
